<template>
  <div v-if="page" class="page page-deep-dive" data-theme="light">
    <div ref="horizontalScrollContainer" class="horizontal-scroll-container">
      <div ref="innerContainer" class="inner">
        <ProjectHeader :project="page" />
        <Modules :modules="page.contentModules" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSeo } from '~/utils/useSeo';
import useHorizontalScrollingNative from '~/utils/useHorizontalScrollingNative';
import Modules from '~/components/modules/Modules.vue';
import ProjectHeader from '~/components/unsorted/ProjectHeader.vue';
import { enterDeepDive } from '~/page-transitions/enterDeepDive';
import { leaveDeepDive } from '~/page-transitions/leaveDeepDive';

const i18n = useI18n();

const { params } = useRoute();
const router = useRouter();

const { data, error } = await useAsyncGql({
  operation: 'GetProjectByTranslatedSlug',
  variables: {
    slug: params.id as string,
    locale: i18n.localeProperties.value.siteLocale,
  },
});

const page = data.value.projectDeepDive;

if (!page || error.value) {
  showError({
    statusCode: 404,
  });
}

if (page) {
  useSeo(page._seoMetaTags ?? []);
}

/* horizontal scroll */
const { setDeepDiveProgress, changeVisibility } = injectStrict(
  ControlPanelInjectionKey,
);
changeVisibility('-faded-in-immediate');

const horizontalScrollContainer = ref<null | HTMLElement>(null);
const innerContainer = ref<null | HTMLElement>(null);

useHorizontalScrollingNative({
  scrollContainer: horizontalScrollContainer,
  onProgressCallback: setDeepDiveProgress,
});

setDeepDiveProgress(0);
definePageMeta({
  middleware: 'adjust-scroll',

  pageTransition: {
    name: '-deep-dive',
    onEnter: (el, done) => {
      const isFromHome = window.RouterHistory
        ? window.RouterHistory.slice(-1)[0] === '/'
        : false;
      enterDeepDive({ el: el, done: done, delay: isFromHome ? 0 : 0.5 });
    },
    onLeave: (el, done) => {
      leaveDeepDive({ el, done });
    },
  },
});

// use Key Events to return to the previous page on ESC
const handleKeyUp = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    router.back();
  }
};

useKeyEvents({
  onKeyUp: handleKeyUp,
});
</script>

<style scoped lang="scss"></style>
